import AddIcon from '@mui/icons-material/Add';
import { Box, Button, Grid, Skeleton, Typography, useMediaQuery, useTheme } from "@mui/material";
import moment from 'moment';
import { lighten } from 'polished';
import { Fragment, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CalendarIcon from '../../icons/CalendarIcon';
import CalendarNoEndIcon from '../../icons/CalendarNoEndIcon';
import DateTimeIcon from '../../icons/DateTimeIcon';
import TimeIcon from '../../icons/TimeIcon';
import { UserContext } from '../../utils/UserContext';
import { MessageContext } from '../../utils/MessageContext';
import { useNavigate } from 'react-router-dom';

export default function Quickstart({ drawer }) {
    const theme = useTheme();
    const belowsm = useMediaQuery(theme.breakpoints.down("sm"));
    const fallbackWidth = belowsm ? window.innerWidth * .43 : window.innerWidth * .13;

    const navigate = useNavigate();

    const { user } = useContext(UserContext);
    const { messages } = useContext(MessageContext);

    const [quickStarts, setQuickStarts] = useState([]);
    const [newQuickStarts, setNewQuickStarts] = useState([false, false, false, false, false]);

    const [width, setWidth] = useState(fallbackWidth);

    const { t } = useTranslation();

    //ensure render of all 4 possible quickStarts
    useEffect(() => {
        if (document.getElementById("favorites-0") && width !== document.getElementById("favorites-0").clientWidth) {
            const newWidth = document.getElementById("favorites-0").clientWidth;
            setWidth(newWidth ? newWidth : fallbackWidth);
        }
        if (user?.quickStart) {
            let fromUser = [...user?.quickStart];

            if (fromUser.length <= 5) {
                const unset = 5 - fromUser.length;
                fromUser = [...fromUser, ...Array(unset).fill(null)]
                const nqs = [];
                for (let i = 0; i < (5 - unset); i++) {
                    nqs.push(false);
                }
                for (let i = 0; i < unset; i++) {
                    nqs.push(true);
                }
                setNewQuickStarts([...nqs]);
            }
            setQuickStarts(fromUser);
        } else {
            setQuickStarts([...Array(5).fill(null)]);
            setNewQuickStarts([true, true, true, true, true]);
        }
        const handleResize = () => {
            const newWidth = document.getElementById("favorites-0").clientWidth;
            setWidth(newWidth ? newWidth : fallbackWidth);
        };
        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
        };
        //eslint-disable-next-line
    }, [user.quickStart, document.getElementById("favorites-0")?.clientWidth])

    const getDifference = (startDate, endDate, companyMessageUid) => {
        const message = messages?.filter(m => m?.companyMessageUid === companyMessageUid)[0];

        const dayStartDate = moment(startDate).set("hour", 0).set("minute", 0).set("second", 0).set("millisecond", 0);
        const dayEndDate = moment(endDate).set("hour", 0).set("minute", 0).set("second", 0).set("millisecond", 0);
        const differenceAsDays = dayEndDate.diff(dayStartDate, "days");
        const differenceAsHours = moment(endDate).diff(moment(startDate), "hours");
        const differenceAsMinutes = moment(endDate).diff(moment(startDate), "minutes");

        if (message?.endDateMandatory === false && message?.endDateTimeMandatory === false && message?.timeOnlyMandatory === false) {
            return t("indefinite")
        }
        if (differenceAsHours > 23 || message?.endDateMandatory || message?.endDateTimeMandatory) {
            const hours = Math.trunc(differenceAsHours - (24 * differenceAsDays));
            const minutes = Math.trunc(differenceAsMinutes - (60 * differenceAsHours));
            if (Math.trunc(differenceAsDays) !== 0 && hours !== 0 && minutes !== 0 && message?.endDateMandatory === false) {
                return `${Math.trunc(differenceAsDays)} ${Math.trunc(differenceAsDays) === 1 ? t("day") : t("days")} & ${hours} ${hours === 1 ? t("hour") : t("hours")} & ${minutes} ${minutes === 1 ? t("minute") : t("minutes")}`;
            }
            if (Math.trunc(differenceAsDays) === 0 && hours !== 0 && minutes !== 0 && message?.endDateMandatory === false) {
                return `${hours} ${hours === 1 ? t("hour") : t("hours")} & ${minutes} ${minutes === 1 ? t("minute") : t("minutes")}`;
            }
            if (hours !== 0 && minutes === 0 && message?.endDateMandatory === false) {
                return `${Math.trunc(differenceAsDays)} ${Math.trunc(differenceAsDays) === 1 ? t("day") : t("days")} & ${hours} ${hours === 1 ? t("hour") : t("hours")}`;
            }
            if (hours === 0 && minutes !== 0 && message?.endDateMandatory === false) {
                return `${Math.trunc(differenceAsDays)} ${Math.trunc(differenceAsDays) === 1 ? t("day") : t("days")} & ${minutes} ${minutes === 1 ? t("minute") : t("minutes")}`;
            }
            return `${Math.trunc(differenceAsDays)} ${Math.trunc(differenceAsDays) === 1 ? t("day") : t("days")}`;
        } else if (differenceAsMinutes > 120) {
            const hours = Math.trunc(differenceAsHours);
            const minutes = Math.trunc(differenceAsMinutes - (60 * differenceAsHours));
            if (minutes === 0) {
                return `${hours} ${hours === 1 ? t("hour") : t("hours")}`;
            }
            return `${hours} ${hours === 1 ? t("hour") : t("hours")} ${t("and")} ${minutes} ${minutes === 1 ? t("minute") : t("minutes")}`;
        } else {
            const minutes = Math.trunc(differenceAsMinutes);
            return `${minutes} ${minutes === 1 ? t("minute") : t("minutes")}`;
        }
    }

    const isAboveOneDay = (start, end) => {
        const diff = moment.duration(moment(end).diff(moment(start)));
        //handle no enddate
        if (!end) {
            return true;
        }
        if (diff.asHours() > 24) {
            return true;
        } else {
            return false;
        }
    }

    const getTitle = (companyMessageUid) => {
        const message = messages?.filter(m => m?.companyMessageUid === companyMessageUid)[0];

        if (message?.timeOnlyMandatory) {
            return t("time-announcement");
        }
        if (message?.endDateTimeMandatory) {
            return t("datetime-announcement");
        }
        if (message?.endDateMandatory) {
            return t("date-announcement")
        }
        if (message?.timeOnlyMandatory === false && message?.endDateTimeMandatory === false && message?.endDateMandatory === false) {
            return t("without-enddate")
        }
        return null;
    };

    const getRepresentation = (companyMessageUid, representation) => {
        const message = messages?.filter(m => m?.companyMessageUid === companyMessageUid)[0];

        if (message?.virtualRepresentation) {
            return t("office-representation");
        };
        if (representation && messages.length !== 0) {
            return representation;
        };
        if (!representation) {
            return t("no-rep")
        }
        return null;
    }

    const getIcon = (companyMessageUid, quickStart, fontSize) => {
        const message = messages?.filter(m => m?.companyMessageUid === companyMessageUid)[0];

        if (message && !message.endDateMandatory && !message.endDateTimeMandatory && !message.timeOnlyMandatory) {
            return <CalendarNoEndIcon fontSize={fontSize} />
        }
        if (message && message?.endDateTimeMandatory) {
            return <DateTimeIcon fontSize={fontSize} />
        }
        if ((quickStart && isAboveOneDay(quickStart?.startDate, quickStart?.endDate)) || (message && message?.endDateMandatory)) {
            return <CalendarIcon fontSize={fontSize} />
        }
        return <TimeIcon fontSize={fontSize} />
    };

    return (
        <Fragment>
            <Box sx={{ display: user?.userID ? "inline" : "none" }} >
                <Grid container sx={{ pl: 1, display: user?.userID ? "inline" : "none" }} >
                    <Grid container direction="row" columnGap={1}>
                        <Grid item xs={5.7}>
                            <Grid container direction="column" height={256}>
                                <Grid item xs={8} sx={{ height: "67%", pb: 1 }}>
                                    <Button
                                        id="favorites-0"
                                        variant="contained"
                                        color="secondary"
                                        onClick={() => {
                                            // Check if there is at least one message with a non-empty announcementSubtitle
                                            const hasAnnouncementSubtitle = Array.isArray(messages) && messages.some(m => typeof m.announcementSubtitle === "string" && m.announcementSubtitle.trim() !== "");

                                            if (belowsm && newQuickStarts[0] && !hasAnnouncementSubtitle) {
                                                navigate("/select?type=favourite");
                                            } else {
                                                drawer(quickStarts[0])
                                            }
                                        }}
                                        disableElevation={belowsm}
                                        sx={{
                                            width: "100%",
                                            height: "100%",
                                            borderRadius: 3,
                                            textTransform: "none",
                                            px: 0,
                                            "&:hover": { bgcolor: lighten(0.02, theme.palette.secondary.main) }
                                        }}
                                        disabled={user.userID === "account-not-active"}
                                    >
                                        {newQuickStarts[0] ?
                                            <Grid container justifyContent="center">
                                                <AddIcon sx={{ fontSize: 32, mr: 2 }} />
                                                <Typography noWrap sx={{ fontSize: "16px", fontStyle: "normal", fontWeight: "normal", lineHeight: "normal", textOverflow: "ellipsis", mt: .7, mr: 2 }}>{t("new-quickstart")}</Typography>
                                            </Grid> :
                                            null
                                        }
                                        {quickStarts[0] ?
                                            <Grid container direction="column" justifyContent="flex-start" sx={{ width: "100%", height: "100%" }}>
                                                <Grid item>
                                                    <Grid container direction="row">
                                                        <Grid item>
                                                            {getIcon(quickStarts[0]?.companyMessageUid, quickStarts[0], 48)}
                                                        </Grid>
                                                        <Grid item>
                                                            {getTitle(quickStarts[0]?.companyMessageUid) ?
                                                                <Typography align="left" noWrap sx={{ fontSize: belowsm ? "11px" : "12px", fontStyle: "normal", fontWeight: 400, lineHeight: "normal", width: width - 80, mt: .5, color: theme.palette.mode === "light" && theme.palette.primary.main === "#0075FF" ? "#4F4F4F" : lighten(0.15, theme.palette.secondary.contrastText) }}>{getTitle(quickStarts[0]?.companyMessageUid)}</Typography> :
                                                                <Skeleton variant="text" animation="wave" sx={{ fontSize: belowsm ? "11px" : "12px", width: width * 0.4, mb: .5 }} />
                                                            }
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                                <Box key="spacer" sx={{ flexGrow: 1 }} />
                                                <Grid container direction="column" sx={{ overflow: "hidden", pl: 1 }}>
                                                    <Grid item>
                                                        <Typography noWrap align="left" sx={{ fontSize: belowsm ? "14px" : "16px", width: width - 25, fontStyle: "normal", fontWeight: 400, lineHeight: "normal", pl: .5, color: "secondary.contrastText" }}>{quickStarts[0]?.endDate ? getDifference(quickStarts[0]?.startDate, quickStarts[0]?.endDate, quickStarts[0]?.companyMessageUid) : t("no-end")}</Typography>
                                                    </Grid>
                                                    <Grid item>
                                                        <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
                                                            {getRepresentation(quickStarts[0]?.companyMessageUid, quickStarts[0]?.representation) ?
                                                                <Typography noWrap align="left" sx={{ fontSize: belowsm ? "12px" : "16px", overflow: "hidden", width: width - 25, fontStyle: "normal", fontWeight: 400, lineHeight: "normal", pl: .5, mt: 0.2, color: theme.palette.mode === "light" && theme.palette.primary.main === "#0075FF" ? "#4F4F4F" : lighten(0.15, theme.palette.secondary.contrastText) }}>{getRepresentation(quickStarts[0]?.companyMessageUid, quickStarts[0]?.representation)}</Typography> :
                                                                <Skeleton variant="text" animation="wave" sx={{ fontSize: belowsm ? "12px" : "16px", width: width * 0.5, ml: .5, mt: .2 }} />
                                                            }
                                                        </Box>
                                                    </Grid>
                                                </Grid>
                                            </Grid> :
                                            null
                                        }
                                    </Button>
                                </Grid>
                                <Grid item xs={4} sx={{ height: "33%", pb: 1 }}>
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        onClick={() => {
                                            // Check if there is at least one message with a non-empty announcementSubtitle
                                            const hasAnnouncementSubtitle = Array.isArray(messages) && messages.some(m => typeof m.announcementSubtitle === "string" && m.announcementSubtitle.trim() !== "");

                                            if (belowsm && newQuickStarts[3] && !hasAnnouncementSubtitle) {
                                                navigate("/select?type=favourite");
                                            } else {
                                                drawer(quickStarts[3])
                                            }
                                        }}
                                        disableElevation={belowsm}
                                        sx={{
                                            width: "100%",
                                            height: "100%",
                                            borderRadius: 3,
                                            textTransform: "none",
                                            px: 0,
                                            "&:hover": { bgcolor: lighten(0.02, theme.palette.secondary.main) }
                                        }}
                                        disabled={user.userID === "account-not-active"}
                                    >
                                        {newQuickStarts[3] ?
                                            <Grid container justifyContent="center">
                                                <AddIcon sx={{ fontSize: 32, mr: 2 }} />
                                                <Typography noWrap sx={{ fontSize: "16px", fontStyle: "normal", fontWeight: "normal", lineHeight: "normal", textOverflow: "ellipsis", mt: .7, mr: 2 }}>{t("new-quickstart")}</Typography>
                                            </Grid> :
                                            null
                                        }
                                        {quickStarts[3] ?
                                            <Box sx={{ width: "100%" }}>
                                                {getTitle(quickStarts[3]?.companyMessageUid) ?
                                                    <Typography noWrap align="left" sx={{ fontSize: belowsm ? "11px" : "12px", fontStyle: "normal", fontWeight: 400, width: width - 15, lineHeight: "normal", pl: 1.5, mb: .5, color: theme.palette.mode === "light" && theme.palette.primary.main === "#0075FF" ? "#4F4F4F" : lighten(0.15, theme.palette.secondary.contrastText) }}>{getTitle(quickStarts[3]?.companyMessageUid)}</Typography> :
                                                    <Skeleton variant="text" animation="wave" sx={{ fontSize: belowsm ? "11px" : "12px", width: width * 0.4, ml: 1.5, mb: .5 }} />
                                                }
                                                <Grid container>
                                                    <Grid item xs={4} sx={{ overflow: "hidden" }}>
                                                        <Grid container sx={{ pl: 1 }}>
                                                            {getIcon(quickStarts[3]?.companyMessageUid, quickStarts[3])}
                                                        </Grid>
                                                    </Grid>
                                                    <Grid item xs={8}>
                                                        <Grid container direction="column" sx={{ overflow: "hidden" }}>
                                                            <Grid item>
                                                                <Typography noWrap align="left" sx={{ fontSize: belowsm ? "14px" : "16px", width: width - 70, fontStyle: "normal", fontWeight: 400, lineHeight: "normal", pl: .5, color: "secondary.contrastText" }}>{quickStarts[3]?.endDate ? getDifference(quickStarts[3]?.startDate, quickStarts[3]?.endDate, quickStarts[3]?.companyMessageUid) : t("no-end")}</Typography>
                                                            </Grid>
                                                            <Grid item>
                                                                {getRepresentation(quickStarts[3]?.companyMessageUid, quickStarts[3]?.representation) ?
                                                                    <Typography noWrap align="left" sx={{ fontSize: belowsm ? "12px" : "16px", overflow: "hidden", width: width - 70, fontStyle: "normal", fontWeight: 400, lineHeight: "normal", pl: .5, mt: .2, color: theme.palette.mode === "light" && theme.palette.primary.main === "#0075FF" ? "#4F4F4F" : lighten(0.15, theme.palette.secondary.contrastText) }}>{getRepresentation(quickStarts[3]?.companyMessageUid, quickStarts[3]?.representation)}</Typography> :
                                                                    <Skeleton variant="text" animation="wave" sx={{ fontSize: belowsm ? "12px" : "16px", width: width * 0.5, ml: .5, mt: .2 }} />
                                                                }
                                                            </Grid>
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                            </Box> :
                                            null
                                        }
                                    </Button>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item xs={5.8}>
                            <Grid container direction="column" height={256}>
                                <Grid item xs={4} sx={{ height: "33%", pb: 1 }}>
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        onClick={() => {
                                            // Check if there is at least one message with a non-empty announcementSubtitle
                                            const hasAnnouncementSubtitle = Array.isArray(messages) && messages.some(m => typeof m.announcementSubtitle === "string" && m.announcementSubtitle.trim() !== "");

                                            if (belowsm && newQuickStarts[1] && !hasAnnouncementSubtitle) {
                                                navigate("/select?type=favourite");
                                            } else {
                                                drawer(quickStarts[1])
                                            }
                                        }}
                                        disableElevation={belowsm}
                                        sx={{
                                            width: "100%",
                                            height: "100%",
                                            borderRadius: 3,
                                            textTransform: "none",
                                            px: 0,
                                            "&:hover": { bgcolor: lighten(0.02, theme.palette.secondary.main) }
                                        }}
                                        disabled={user.userID === "account-not-active"}
                                    >
                                        {newQuickStarts[1] ?
                                            <Grid container justifyContent="center">
                                                <AddIcon sx={{ fontSize: 32, mr: 2 }} />
                                                <Typography noWrap sx={{ fontSize: "16px", fontStyle: "normal", fontWeight: "normal", lineHeight: "normal", textOverflow: "ellipsis", mt: .7, mr: 2 }}>{t("new-quickstart")}</Typography>
                                            </Grid> :
                                            null
                                        }
                                        {quickStarts[1] ?
                                            <Box sx={{ width: "100%" }}>
                                                <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
                                                    {getTitle(quickStarts[1]?.companyMessageUid) ?
                                                        <Typography noWrap align="left" sx={{ fontSize: belowsm ? "11px" : "12px", width: width - 15, fontStyle: "normal", fontWeight: 400, lineHeight: "normal", pl: 1.5, mb: .5, color: theme.palette.mode === "light" && theme.palette.primary.main === "#0075FF" ? "#4F4F4F" : lighten(0.15, theme.palette.secondary.contrastText) }}>{getTitle(quickStarts[1]?.companyMessageUid)}</Typography> :
                                                        <Skeleton variant="text" animation="wave" sx={{ fontSize: belowsm ? "11px" : "12px", width: width * 0.4, ml: 1.5, mb: .5 }} />
                                                    }
                                                </Box>
                                                <Grid container>
                                                    <Grid item xs={4} sx={{ overflow: "hidden" }}>
                                                        <Grid container sx={{ pl: 1 }}>
                                                            {getIcon(quickStarts[1]?.companyMessageUid, quickStarts[1])}
                                                        </Grid>
                                                    </Grid>
                                                    <Grid item xs={8}>
                                                        <Grid container direction="column" sx={{ overflow: "hidden" }}>
                                                            <Grid item>
                                                                <Typography noWrap align="left" sx={{ fontSize: belowsm ? "14px" : "16px", width: width - 70, fontStyle: "normal", fontWeight: 400, lineHeight: "normal", pl: .5, color: "secondary.contrastText" }}>{quickStarts[1]?.endDate ? getDifference(quickStarts[1]?.startDate, quickStarts[1]?.endDate, quickStarts[1]?.companyMessageUid) : t("no-end")}</Typography>
                                                            </Grid>
                                                            <Grid item>
                                                                {getRepresentation(quickStarts[1]?.companyMessageUid, quickStarts[1]?.representation) ?
                                                                    <Typography noWrap align="left" sx={{ fontSize: belowsm ? "12px" : "16px", overflow: "hidden", width: width - 70, fontStyle: "normal", fontWeight: 400, lineHeight: "normal", pl: .5, mt: .2, color: theme.palette.mode === "light" && theme.palette.primary.main === "#0075FF" ? "#4F4F4F" : lighten(0.15, theme.palette.secondary.contrastText) }}>{getRepresentation(quickStarts[1]?.companyMessageUid, quickStarts[1]?.representation)}</Typography> :
                                                                    <Skeleton variant="text" animation="wave" sx={{ fontSize: belowsm ? "12px" : "16px", width: width * 0.5, ml: .5, mt: .2 }} />
                                                                }
                                                            </Grid>
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                            </Box> :
                                            null
                                        }
                                    </Button>
                                </Grid>
                                <Grid item xs={4} sx={{ height: "33%", pb: 1 }}>
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        onClick={() => {
                                            // Check if there is at least one message with a non-empty announcementSubtitle
                                            const hasAnnouncementSubtitle = Array.isArray(messages) && messages.some(m => typeof m.announcementSubtitle === "string" && m.announcementSubtitle.trim() !== "");

                                            if (belowsm && newQuickStarts[2] && !hasAnnouncementSubtitle) {
                                                navigate("/select?type=favourite");
                                            } else {
                                                drawer(quickStarts[2])
                                            }
                                        }}
                                        disableElevation={belowsm}
                                        sx={{
                                            width: "100%",
                                            height: "100%",
                                            borderRadius: 3,
                                            textTransform: "none",
                                            px: 0,
                                            "&:hover": { bgcolor: lighten(0.02, theme.palette.secondary.main) }
                                        }}
                                        disabled={user.userID === "account-not-active"}
                                    >
                                        {newQuickStarts[2] ?
                                            <Grid container justifyContent="center">
                                                <AddIcon sx={{ fontSize: 32, mr: 2 }} />
                                                <Typography noWrap sx={{ fontSize: "16px", fontStyle: "normal", fontWeight: "normal", lineHeight: "normal", textOverflow: "ellipsis", mt: .7, mr: 2 }}>{t("new-quickstart")}</Typography>
                                            </Grid> :
                                            null
                                        }
                                        {quickStarts[2] ?
                                            <Box sx={{ width: "100%" }}>
                                                <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
                                                    {getTitle(quickStarts[2]?.companyMessageUid) ?
                                                        <Typography noWrap align="left" sx={{ fontSize: belowsm ? "11px" : "12px", width: width - 15, fontStyle: "normal", fontWeight: 400, lineHeight: "normal", pl: 1.5, mb: .5, color: theme.palette.mode === "light" && theme.palette.primary.main === "#0075FF" ? "#4F4F4F" : lighten(0.15, theme.palette.secondary.contrastText) }}>{getTitle(quickStarts[2]?.companyMessageUid)}</Typography> :
                                                        <Skeleton variant="text" animation="wave" sx={{ fontSize: belowsm ? "11px" : "12px", width: width * 0.4, ml: 1.5, mb: .5 }} />
                                                    }
                                                </Box>
                                                <Grid container>
                                                    <Grid item xs={4} sx={{ overflow: "hidden" }}>
                                                        <Grid container>
                                                            <Grid container sx={{ pl: 1 }}>
                                                                {getIcon(quickStarts[2]?.companyMessageUid, quickStarts[2])}
                                                            </Grid>
                                                        </Grid>
                                                    </Grid>
                                                    <Grid item xs={8}>
                                                        <Grid container direction="column" sx={{ overflow: "hidden" }}>
                                                            <Grid item>
                                                                <Typography noWrap align="left" sx={{ fontSize: belowsm ? "14px" : "16px", width: width - 70, fontStyle: "normal", fontWeight: 400, lineHeight: "normal", pl: .5, color: "secondary.contrastText" }}>{quickStarts[2]?.endDate ? getDifference(quickStarts[2]?.startDate, quickStarts[2]?.endDate, quickStarts[2]?.companyMessageUid) : t("no-end")}</Typography>
                                                            </Grid>
                                                            <Grid item>
                                                                {getRepresentation(quickStarts[2]?.companyMessageUid, quickStarts[2]?.representation) ?
                                                                    <Typography noWrap align="left" sx={{ fontSize: belowsm ? "12px" : "16px", overflow: "hidden", width: width - 70, fontStyle: "normal", fontWeight: 400, lineHeight: "normal", pl: .5, mt: .2, color: theme.palette.mode === "light" && theme.palette.primary.main === "#0075FF" ? "#4F4F4F" : lighten(0.15, theme.palette.secondary.contrastText) }}>{getRepresentation(quickStarts[2]?.companyMessageUid, quickStarts[2]?.representation)}</Typography> :
                                                                    <Skeleton variant="text" animation="wave" sx={{ fontSize: belowsm ? "12px" : "16px", width: width * 0.5, ml: .5, mt: .2 }} />
                                                                }
                                                            </Grid>
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                            </Box> :
                                            null
                                        }
                                    </Button>
                                </Grid>
                                <Grid item xs={4} sx={{ height: "33%", pb: 1 }}>
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        onClick={() => {
                                            // Check if there is at least one message with a non-empty announcementSubtitle
                                            const hasAnnouncementSubtitle = Array.isArray(messages) && messages.some(m => typeof m.announcementSubtitle === "string" && m.announcementSubtitle.trim() !== "");

                                            if (belowsm && newQuickStarts[4] && !hasAnnouncementSubtitle) {
                                                navigate("/select?type=favourite");
                                            } else {
                                                drawer(quickStarts[4])
                                            }
                                        }}
                                        disableElevation={belowsm}
                                        sx={{
                                            width: "100%",
                                            height: "100%",
                                            borderRadius: 3,
                                            textTransform: "none",
                                            px: 0,
                                            "&:hover": { bgcolor: lighten(0.02, theme.palette.secondary.main) }
                                        }}
                                        disabled={user.userID === "account-not-active"}
                                    >
                                        {newQuickStarts[4] ?
                                            <Grid container justifyContent="center">
                                                <AddIcon sx={{ fontSize: 32 }} />
                                                <Typography noWrap sx={{ fontSize: "16px", fontStyle: "normal", fontWeight: "normal", lineHeight: "normal", textOverflow: "ellipsis", mt: .7, mx: 2 }}>{t("new-quickstart")}</Typography>
                                            </Grid> :
                                            null
                                        }
                                        {quickStarts[4] ?
                                            <Box sx={{ width: "100%" }}>
                                                <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
                                                    {getTitle(quickStarts[4]?.companyMessageUid) ?
                                                        <Typography noWrap align="left" sx={{ fontSize: belowsm ? "11px" : "12px", width: width - 15, fontStyle: "normal", fontWeight: 400, lineHeight: "normal", pl: 1.5, mb: .5, color: theme.palette.mode === "light" && theme.palette.primary.main === "#0075FF" ? "#4F4F4F" : lighten(0.15, theme.palette.secondary.contrastText) }}>{getTitle(quickStarts[4]?.companyMessageUid)}</Typography> :
                                                        <Skeleton variant="text" animation="wave" sx={{ fontSize: belowsm ? "11px" : "12px", width: width * 0.4, ml: 1.5, mb: .5 }} />
                                                    }
                                                </Box>
                                                <Grid container>
                                                    <Grid item xs={4} sx={{ overflow: "hidden" }}>
                                                        <Grid container>
                                                            <Grid container sx={{ pl: 1 }}>
                                                                {getIcon(quickStarts[4]?.companyMessageUid, quickStarts[4])}
                                                            </Grid>
                                                        </Grid>
                                                    </Grid>
                                                    <Grid item xs={8}>
                                                        <Grid container direction="column" sx={{ overflow: "hidden" }}>
                                                            <Grid item>
                                                                <Typography noWrap align="left" sx={{ fontSize: belowsm ? "14px" : "16px", width: width - 70, fontStyle: "normal", fontWeight: 400, lineHeight: "normal", pl: .5, color: "secondary.contrastText" }}>{quickStarts[4]?.endDate ? getDifference(quickStarts[4]?.startDate, quickStarts[4]?.endDate, quickStarts[4]?.companyMessageUid) : t("no-end")}</Typography>
                                                            </Grid>
                                                            <Grid item>
                                                                {getRepresentation(quickStarts[4]?.companyMessageUid, quickStarts[4]?.representation) ?
                                                                    <Typography noWrap align="left" sx={{ fontSize: belowsm ? "12px" : "16px", overflow: "hidden", width: width - 70, fontStyle: "normal", fontWeight: 400, lineHeight: "normal", pl: .5, mt: .2, color: theme.palette.mode === "light" && theme.palette.primary.main === "#0075FF" ? "#4F4F4F" : lighten(0.15, theme.palette.secondary.contrastText) }}>{getRepresentation(quickStarts[4]?.companyMessageUid, quickStarts[4]?.representation)}</Typography> :
                                                                    <Skeleton variant="text" animation="wave" sx={{ fontSize: belowsm ? "12px" : "16px", width: width * 0.5, ml: .5, mt: .2 }} />
                                                                }
                                                            </Grid>
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                            </Box> :
                                            null
                                        }
                                    </Button>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Box>
            <Box sx={{ display: !user?.userID ? "inline" : "none" }}>
                <Grid container direction="row" columnGap={1} sx={{ mt: belowsm ? 1 : 4 }}>
                    <Grid item xs={5.8}>
                        <Grid container direction="column" height={256}>
                            <Grid item xs={8} sx={{ height: "67%", pb: 1 }}>
                                <Skeleton animation="wave" variant="rectangular" sx={{ borderRadius: 3, height: "100%" }} />
                            </Grid>
                            <Grid item xs={4} sx={{ height: "33%", pb: 1 }}>
                                <Skeleton animation="wave" variant="rectangular" sx={{ borderRadius: 3, height: "100%" }} />
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item xs={5.8}>
                        <Grid container direction="column" height={256}>
                            <Grid item xs={4} sx={{ height: "33%", pb: 1 }}>
                                <Skeleton animation="wave" variant="rectangular" sx={{ borderRadius: 3, height: "100%" }} />
                            </Grid>
                            <Grid item xs={4} sx={{ height: "33%", pb: 1 }}>
                                <Skeleton animation="wave" variant="rectangular" sx={{ borderRadius: 3, height: "100%" }} />
                            </Grid>
                            <Grid item xs={4} sx={{ height: "33%", pb: 1 }}>
                                <Skeleton animation="wave" variant="rectangular" sx={{ borderRadius: 3, height: "100%" }} />
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Box>
        </Fragment>
    )
};